/**
 * Frontend → backend PDF generation utility.
 *
 * Calls the Playwright-backed FastAPI endpoint `/api/pdf/render` and triggers
 * an attachment download with the resulting PDF bytes.
 *
 * Session forwarding:
 *  - httpOnly cookies (preview)  → browser ships them via credentials:'include'
 *  - localStorage Bearer token (production fallback) → read here and forwarded
 *    in the body so the headless Chromium instance can replay it on the
 *    voucher route's first paint.
 *
 * Usage:
 *   await generatePDF({
 *     pageUrl: 'https://app.example.com/voucher/abc',
 *     filename: 'voucher_abc.pdf',
 *     options: { format: 'A4', landscape: false }
 *   });
 */
export async function generatePDF({ pageUrl, filename, options = {}, waitUntil, waitSelector, cacheKey }) {
  const accessToken = localStorage.getItem('trv_access_token') || null;

  const response = await fetch('/api/pdf/render', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      url: pageUrl,
      filename: filename || 'document.pdf',
      options,
      accessToken,
      waitUntil: waitUntil || null,
      waitSelector: waitSelector || null,
      cacheKey: cacheKey || null,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`PDF generation failed: ${error}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'document.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
